import React, { useState, useRef } from 'react';
import * as investorContactClient from '../../models/investorContacts/investorContactClient';
import { ClientError } from '../../../common/http/errors';

interface Props {
  incomeOptions: [string, number][];
  assetsOptions: [string, number][];
  interestTypeOptions: [string, number][];
  qualificationOptions: [string, number][];
  qualificationNone: number;
}

interface InputProps {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  errors?: string[];
}

interface SelectProps {
  id: string;
  label: string;
  errorLabel?: string;
  subLabel?: string;
  value: string;
  options: [string, number][];
  onChange: (value: string) => void;
  errors?: string[];
}

interface Errors {
  name?: string[];
  email?: string[];
  phone?: string[];
  income?: string[];
  assets?: string[];
  qualification?: string[];
  interestType?: string[];
  requestedSalesInfo?: string[];
  privacyConfirmed?: string[];
}

const InvestorContactFormView = (props: Props) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    income: '',
    assets: '',
    interestType: '',
    requestedSalesInfo: '',
    privacyConfirmed: false,
  });
  const [qualifications, setQualification] = useState<number[]>([]);
  const [disabledqualification, setdisabledqualification] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  // 項目単位のエラーが返らない 4xx（範囲外の値など）でサーバの文言をそのまま出すための枠
  const [generalError, setGeneralError] = useState<string | null>(null);
  const formTopRef = useRef<HTMLHeadingElement>(null);
  const [loading, setLoading] = useState(false); // 二重送信防止

  const handleChange = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [key]: value,
    }));

    // 入力されたらリセット
    setErrors((prevErrors: Errors): Errors => {
      const newErrors = { ...prevErrors };
      if (newErrors[key as keyof Errors]) {
        delete newErrors[key as keyof Errors];
      }
      return newErrors;
    });
  };

  const handleCheckboxChange = (key: string, value: boolean) => {
    setFormData(prev => ({
      ...prev,
      [key]: value,
    }));

    // 入力されたらリセット
    setErrors((prevErrors: Errors): Errors => {
      const newErrors = { ...prevErrors };
      if (newErrors[key as keyof Errors]) {
        delete newErrors[key as keyof Errors];
      }
      return newErrors;
    });
  };

  const handleChangeQualification = (id: number) => {
    setQualification((prev: number[]) =>
      prev.includes(id)
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id],
    );

    setErrors((prevErrors: Errors): Errors => {
      const newErrors = { ...prevErrors };
      if (newErrors.qualification) {
        delete newErrors.qualification;
      }
      return newErrors;
    });
  };

  const handleResetQualification = (value: boolean) => {
    setQualification([]);
    setdisabledqualification(value);

    setErrors((prevErrors: Errors): Errors => {
      const newErrors = { ...prevErrors };
      if (newErrors.qualification) {
        delete newErrors.qualification;
      }
      return newErrors;
    });
  };

  // 返却パラメータの加工用
  const toNumOrNull = (value: string) => {
    const num = Number(value);
    return isNaN(num) || value === '' ? null : num;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (loading) return;

    setLoading(true);

    let qualificationParam;
    if (disabledqualification) {
      // 全く当てはまらない選択時
      qualificationParam = [props.qualificationNone];
    } else {
      // 何も選択されていなければ、nullを返してサーバ側でエラーを返す
      qualificationParam = qualifications.length > 0 ? qualifications : null;
    }

    investorContactClient
      .create({
        investor_contact: {
          name: formData.name,
          email: formData.email,
          income: toNumOrNull(formData.income),
          assets: toNumOrNull(formData.assets),
          phone: formData.phone,
          qualification: qualificationParam,
          interest_type: toNumOrNull(formData.interestType),
          requested_sales_info: toNumOrNull(formData.requestedSalesInfo),
          privacy_confirmed: formData.privacyConfirmed,
        },
      })
      .then(() => {
        window.location.href = '/plus/investor_contact_thanks';
      })
      .catch((error: unknown) => {
        if (error instanceof ClientError && error.status === 422) {
          // errors が無い 422 もある（サーバ共通のエラーハンドラが返す形）ので、
          // その場合は項目単位の表示をあきらめて message を出す
          // body を読めなかったときは null になる
          const data = (error.body ?? {}) as {
            message?: string;
            errors?: Record<string, string[]>;
          };
          setErrors(data.errors ?? {});
          setGeneralError(data.errors == null ? (data.message ?? null) : null);

          if (formTopRef.current) {
            formTopRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        } else {
          console.warn(error);
          alert('エラーが発生しました。ページを再読み込みしてください。');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="lp-pattern-inner">
      <h2 ref={formTopRef} className="section_title">
        お問い合わせフォーム
      </h2>
      <p className="lp-form-text">
        特定の条件を満たす方にのみ情報開示が可能なため現在取り扱い中の案件や、
        <br className="sp-none" />
        サービスについて質問は以下フォームよりお問い合わせください。
      </p>
      {Object.keys(errors).length > 0 && (
        <div className="error-block">未入力の必須項目があります。</div>
      )}
      {generalError && <div className="error-block">{generalError}</div>}
      <div className="form-wrap">
        <InputField
          id="name"
          label="お名前"
          value={formData.name}
          placeholder="投資 太郎"
          onChange={value => handleChange('name', value)}
          errors={errors.name}
        />
        <InputField
          id="email"
          label="メールアドレス"
          value={formData.email}
          placeholder="example@fundinno.co.jp"
          onChange={value => handleChange('email', value)}
          errors={errors.email}
        />
        <InputField
          id="phone"
          label="電話番号"
          value={formData.phone}
          placeholder="08011112222"
          onChange={value => handleChange('phone', value)}
          errors={errors.phone}
        />
        <SelectField
          id="income"
          label="年収"
          value={formData.income}
          options={props.incomeOptions}
          onChange={value => handleChange('income', value)}
          errors={errors.income}
        />
        <SelectField
          id="assets"
          label="資産"
          value={formData.assets}
          options={props.assetsOptions}
          onChange={value => handleChange('assets', value)}
          errors={errors.assets}
        />
        <div className="customer-info-group">
          <div className="customer-info-group-label twocolumn">
            <div className="customer-info-group-label-text">
              専門知識の有無
              <span className="text-left">
                (いずれかに従事した期間が通算一年以上)
              </span>
            </div>
          </div>
          <div className="customer-info-group-input">
            <fieldset
              className={`form__checkbox ${
                errors.qualification ? 'is-invalid' : ''
              }`}
            >
              {props.qualificationOptions.map(([label, value]) => (
                <label
                  key={value}
                  htmlFor={`qualification-${value}`}
                  className=""
                >
                  {label}
                  <input
                    type="checkbox"
                    id={`qualification-${value}`}
                    checked={qualifications.includes(value)}
                    onChange={() => handleChangeQualification(value)}
                    disabled={disabledqualification}
                  />
                </label>
              ))}
              <label htmlFor="qualification-reset">
                当てはまる項目がない
                <input
                  type="checkbox"
                  id="qualification-reset"
                  checked={disabledqualification}
                  onChange={e => handleResetQualification(e.target.checked)}
                />
              </label>
            </fieldset>
            {errors.qualification && errors.qualification.length > 0 && (
              <label className="validation-error error">
                {errors.qualification.map(
                  message => `専門知識の有無${message}`,
                )}
              </label>
            )}
          </div>
        </div>
        <SelectField
          id="interestType"
          label="ご興味のある案件"
          value={formData.interestType}
          options={props.interestTypeOptions}
          onChange={value => handleChange('interestType', value)}
          errors={errors.interestType}
        />
        <SelectField
          id="requestedSalesInfo"
          label="営業からのご案内を希望しますか？"
          value={formData.requestedSalesInfo}
          errorLabel={'ご案内の可否'}
          options={[
            ['はい', 1],
            ['いいえ', 0],
          ]}
          onChange={value => handleChange('requestedSalesInfo', value)}
          errors={errors.requestedSalesInfo}
        />
      </div>
      <div className="form-btn">
        <div className="agreements-checkbox">
          <input
            type="checkbox"
            name=""
            id="privacy_confirmed"
            value={formData.privacyConfirmed.toString()}
            className="agreements-check"
            onChange={e =>
              handleCheckboxChange('privacyConfirmed', e.target.checked)
            }
          />
          <label
            className={`privacy_confirmed_label ${
              errors.privacyConfirmed ? 'is-invalid' : ''
            }`}
            htmlFor="privacy_confirmed"
          >
            当社の
            <a href="/privacy_policy" target="_blank">
              個人情報の取扱い
            </a>
            に同意する
          </label>
          {errors.privacyConfirmed && errors.privacyConfirmed.length > 0 && (
            <label className="validation-error error">
              {errors.privacyConfirmed.map(message => message)}
            </label>
          )}
        </div>
        <a className="agreements-link" href="#" onClick={handleSubmit}>
          お問い合わせする
        </a>
        <p>
          ※特定投資家のご登録条件に該当しない方には、案件についての情報はご案内できない可能性があります。
          <br className="sp-none" />
          特定投資家のご登録条件は、「特定投資家のご登録条件について」より、ご確認ください。
        </p>
      </div>
    </div>
  );
};

const InputField = ({
  id,
  label,
  value,
  placeholder,
  onChange,
  errors,
}: InputProps) => (
  <div className="customer-info-group">
    <div className="customer-info-group-label">
      <div className="customer-info-group-label-text">{label}</div>
    </div>
    <div className="customer-info-group-input">
      <input
        className={`form-control ${errors ? 'is-invalid' : ''} ${
          value === '' ? 'is-empty' : ''
        }`}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
      {errors && errors.length > 0 && (
        <label className="validation-error error" htmlFor={id}>
          {errors.map(message => `${label}${message}`)}
        </label>
      )}
    </div>
  </div>
);

const SelectField = ({
  id,
  label,
  errorLabel,
  subLabel,
  value,
  options,
  onChange,
  errors,
}: SelectProps) => (
  <div className="customer-info-group">
    <div className="customer-info-group-label">
      <div className="customer-info-group-label-text inline-text">
        {label}
        {subLabel && <span>{`(${subLabel})`}</span>}
      </div>
    </div>
    <div className="customer-info-group-input">
      <fieldset className="form-group select">
        <select
          id={id}
          className={`form-control ${errors ? 'is-invalid' : ''} ${
            value === '' ? 'is-empty' : ''
          }`}
          value={value}
          onChange={e => onChange(e.target.value)}
        >
          <option value="">選択してください</option>
          {options.map(([label, value]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </fieldset>
      {errors && errors.length > 0 && (
        <label className="validation-error error" htmlFor={id}>
          {errors.map(message => `${errorLabel ?? label}${message}`)}
        </label>
      )}
    </div>
  </div>
);

const renderView = (props: Props) => {
  return <InvestorContactFormView {...props} />;
};

export default renderView;
